import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, Compass, SkipForward, Info, CheckCircle2 } from "lucide-react";
import Seo from "@/components/Seo";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import TimezoneSelect from "@/components/TimezoneSelect";
import StrategicGoalWizard from "@/app/components/StrategicGoalWizard";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { track } from "@/lib/analytics";

const INDUSTRIES = ["SaaS / Software", "Agency / Services", "Consulting", "E-commerce / Retail", "Creative / Media", "Finance", "Healthcare", "Education", "Real Estate", "Other"];
const TEAM_SIZES = ["Just me", "1-5", "6-15", "16-50", "50+"];
const ENTITY_TYPES = [
  "Startup",
  "Small Business / SME",
  "Agency",
  "Freelancer",
  "Consultant",
  "Professional / Individual",
  "Nonprofit / Social Enterprise",
  "Other",
];
const SOURCE_OPTIONS = [
  "Google / Search Engine",
  "Social Media",
  "YouTube",
  "Friend / Colleague Referral",
  "StUpEco Client / Partner",
  "Event / Community",
  "Email / Newsletter",
  "Other",
];

const inputCls = "w-full rounded-xl border border-border bg-card px-4 py-2.5 text-sm outline-none transition-colors focus:border-primary";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("profile"); // profile -> strategic
  const [strategicOpen, setStrategicOpen] = useState(false);
  const [strategicComplete, setStrategicComplete] = useState(false);
  const [form, setForm] = useState({
    workspace_name: user?.name ? `${user.name.split(" ")[0]}'s Workspace` : "My Workspace",
    business_name: "",
    industry: "SaaS / Software",
    entity_type: "Startup",
    country: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
    team_size: "Just me",
    working_style: "",
    source_of_signup: "",
    source_of_signup_other: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const finishOnboarding = async () => {
    await checkAuth();
    toast.success("Your workspace is ready.");
    navigate("/app");
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form };
      if (payload.source_of_signup !== "Other") delete payload.source_of_signup_other;
      await api.post("/onboarding", payload);
      track("onboarding_complete", { industry: form.industry, team_size: form.team_size, source: form.source_of_signup });
      setStep("strategic");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const stepLabel = step === "profile"
    ? "Step 1 of 2 · Your workspace profile"
    : "Step 2 of 2 · Set your direction";

  const heading = step === "profile"
    ? "Tell us about your work."
    : "Set your business direction.";

  const subheading = step === "profile"
    ? "Your workspace uses this to personalise everything — your briefing, recommendations and daily workspace."
    : "Define one Primary Strategic Goal. Workspace drafts a stage-by-stage roadmap you can approve, edit or skip.";

  return (
    <div className="relative min-h-screen">
      <Seo title="Set up your workspace" description="Tell StUpEco Workspace about your work." path="/app/onboarding" />
      <div className="noise-overlay" aria-hidden="true" />
      <div className="pointer-events-none absolute left-1/2 top-0 h-[420px] w-[760px] -translate-x-1/2 rounded-full opacity-[0.12] blur-[120px]" style={{ background: "var(--brand-gradient)" }} aria-hidden="true" />

      <header className="relative z-10 mx-auto flex max-w-3xl items-center justify-between px-5 py-5 sm:px-8">
        <Logo imgClass="h-7 sm:h-8" />
        <ThemeToggle />
      </header>

      <main className="relative z-10 mx-auto max-w-2xl px-5 py-8 sm:px-8 sm:py-12" data-testid="onboarding-page">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/60 glass px-4 py-1.5 text-xs font-medium text-muted-foreground">
            <Sparkles className="h-3.5 w-3.5 text-brand-teal" /> {stepLabel}
          </span>
          <h1 className="mt-5 font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">{heading}</h1>
          <p className="mt-3 text-sm text-muted-foreground sm:text-base">{subheading}</p>
        </motion.div>

        {step === "profile" && (
        <form onSubmit={submit} className="mt-8 rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="onboarding-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium">Workspace name</label>
              <input required value={form.workspace_name} onChange={update("workspace_name")} className={inputCls} data-testid="onboarding-workspace-name" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 flex items-center gap-1.5 text-sm font-medium">
                Business name / Professional identity
                <span className="group relative inline-flex" data-testid="onboarding-business-name-tooltip">
                  <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                  <span className="pointer-events-none absolute left-0 top-full z-10 mt-1 hidden w-64 rounded-lg border border-border bg-popover p-2 text-[11px] leading-snug text-popover-foreground shadow-lg group-hover:block group-focus-within:block">
                    Don't have a business? Freelancers, consultants and working professionals can enter their professional title, practice name or their own name.
                  </span>
                </span>
              </label>
              <input required value={form.business_name} onChange={update("business_name")} className={inputCls} data-testid="onboarding-business-name" placeholder="Acme Analytics, or Priya Sharma — Independent Consultant" />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Industry</label>
              <select value={form.industry} onChange={update("industry")} className={inputCls} data-testid="onboarding-industry">
                {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Entity type</label>
              <select value={form.entity_type} onChange={update("entity_type")} className={inputCls} data-testid="onboarding-entity-type">
                {ENTITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Team size</label>
              <select value={form.team_size} onChange={update("team_size")} className={inputCls} data-testid="onboarding-team-size">
                {TEAM_SIZES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium">Country</label>
              <input required value={form.country} onChange={update("country")} className={inputCls} data-testid="onboarding-country" placeholder="India" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium">Time zone</label>
              <TimezoneSelect value={form.timezone} onChange={(v) => setForm((f) => ({ ...f, timezone: v }))} testId="onboarding-timezone" />
              <p className="mt-1 text-xs text-muted-foreground">We pre-fill with your browser's time zone — change it to your workspace's operating zone.</p>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium">Working style <span className="text-muted-foreground">(optional)</span></label>
              <input value={form.working_style} onChange={update("working_style")} className={inputCls} data-testid="onboarding-working-style" placeholder="e.g. Async, morning deep work, weekly reviews" />
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-medium">How did you hear about us?</label>
              <select required value={form.source_of_signup} onChange={update("source_of_signup")} className={inputCls} data-testid="onboarding-source-of-signup">
                <option value="" disabled>Select an option</option>
                {SOURCE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              {form.source_of_signup === "Other" && (
                <input value={form.source_of_signup_other} onChange={update("source_of_signup_other")} placeholder="Tell us where you heard about us" className={`${inputCls} mt-2`} data-testid="onboarding-source-of-signup-other" />
              )}
            </div>
          </div>

          <button type="submit" disabled={loading} data-testid="onboarding-submit" className="group mt-6 inline-flex w-full items-center justify-center gap-1.5 rounded-full brand-gradient-bg px-6 py-3.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:shadow-lg disabled:opacity-60">
            {loading ? "Setting up your workspace…" : "Continue: set your direction"}
            {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
          </button>
        </form>
        )}

        {step === "strategic" && (
          <div className="mt-8 rounded-3xl border border-border bg-card p-6 sm:p-8" data-testid="onboarding-strategic">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal"><Compass className="h-5 w-5" /></span>
              <div>
                <h2 className="font-display text-lg font-semibold tracking-tight">Define your Primary Strategic Goal</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  This is the north star of your business. Where you are today, where you're going, and by when. Your AI then drafts the roadmap.
                </p>
                {strategicComplete && (
                  <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-brand-green"><CheckCircle2 className="h-3.5 w-3.5" /> Primary Strategic Goal set. You can add more anytime.</p>
                )}
              </div>
            </div>
            <div className="mt-5 flex flex-col gap-2 sm:flex-row">
              {!strategicComplete && (
                <button onClick={() => setStrategicOpen(true)} data-testid="onboarding-open-strategic" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full brand-gradient-bg px-5 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5">
                  <Sparkles className="h-4 w-4" /> Define with AI
                </button>
              )}
              <button onClick={finishOnboarding} data-testid="onboarding-continue-founding" className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-full border border-border bg-background px-5 py-3 text-sm font-semibold text-foreground hover:bg-muted">
                {strategicComplete ? (<><ArrowRight className="h-4 w-4" /> Enter your workspace</>) : (<><SkipForward className="h-4 w-4" /> Skip — I'll do this later</>)}
              </button>
            </div>
            <p className="mt-3 text-center text-xs text-muted-foreground">
              You can always define or edit your Strategic Goals from the Goals module.
            </p>
          </div>
        )}
      </main>

      <StrategicGoalWizard
        open={strategicOpen}
        onOpenChange={setStrategicOpen}
        defaultRole="primary"
        hideRole
        hidePriority
        onCreated={() => { setStrategicOpen(false); setStrategicComplete(true); }}
      />
    </div>
  );
}
